import { initGa4, shouldLoadAnalytics } from '@/lib/analytics';
import { shouldRegisterServiceWorker } from '@/lib/desktop';
import * as Sentry from '@sentry/react';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { sanitizeSentryBreadcrumb } from './lib/telemetry';
import { getRouter } from './router';
import './styles.css';

// Sentry：有 VITE_SENTRY_DSN 就启用；release 由 vite 把它 baked 进 bundle。
// PWA / Tauri 通过 environment 区分，方便 dashboard 里 group。
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    sendDefaultPii: false,
    beforeBreadcrumb: sanitizeSentryBreadcrumb,
    environment: typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window ? 'tauri' : 'web',
    release: __APP_COMMIT__,
  });
}

// GA4：VITE_GA4_MEASUREMENT_ID 未设 = no-op；桌面壳与 DNT 用户不加载（见 lib/analytics）。
const ga4Id = import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined;
if (
  typeof window !== 'undefined' &&
  shouldLoadAnalytics({
    measurementId: ga4Id,
    isDesktop: '__TAURI_INTERNALS__' in window,
    doNotTrack: navigator.doNotTrack,
  })
) {
  initGa4(ga4Id as string);
}

// PWA service worker 只在真浏览器注册（见 shouldRegisterServiceWorker 的 why）：
// macOS Tauri 是 tauri:// 协议会抛错；Windows/Linux Tauri 是 http(s) 形态协议，
// 协议启发式挡不住 → 必须叠加 isDesktop() 门。vite-plugin-pwa 虚拟模块手动 register
// （plugin 侧 injectRegister 设成 null）。
if (typeof window !== 'undefined' && shouldRegisterServiceWorker(window.location.protocol)) {
  void import('virtual:pwa-register').then(({ registerSW }) => {
    registerSW({ immediate: true });
  });
}

const router = getRouter();

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root not found');

createRoot(rootEl).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
